import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { 
  PiggyBank, Wallet, TrendingUp, BadgeCheck, Accessibility, Building, 
  GraduationCap, BarChart3, Lock, Home, DollarSign, Users, Briefcase,
  ArrowRight, ExternalLink, Shield, Landmark
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

// Translations for Investment page
const pageText = {
  en: {
    hero: {
      title: 'Investment Solutions',
      subtitle: 'Build your wealth with smart investment strategies and tax-advantaged accounts',
      cta: 'Book a Consultation'
    },
    registered: {
      title: 'Tax-Advantaged Registered Accounts',
      subtitle: 'Maximize your savings with government-registered investment accounts',
      rrsp: { title: 'RRSP', desc: 'Registered Retirement Savings Plan - Tax-deductible contributions that grow tax-free until retirement.' },
      tfsa: { title: 'TFSA', desc: 'Tax-Free Savings Account - Flexible savings with completely tax-free growth and withdrawals.' },
      fhsa: { title: 'FHSA', desc: 'First Home Savings Account - Tax-deductible contributions for your first home purchase.' },
      resp: { title: 'RESP', desc: 'Registered Education Savings Plan - Save for your child\'s education with government grants up to $7,200.' }
    },
    specialized: {
      title: 'Specialized Registered Accounts',
      subtitle: 'Additional registered options for specific needs',
      rdsp: { title: 'RDSP', desc: 'Registered Disability Savings Plan - Long-term savings for Canadians with disabilities, with government matching grants.' },
      lira: { title: 'LIRA', desc: 'Locked-In Retirement Account - Protect and grow your pension funds when you leave an employer.' },
      lif: { title: 'LIF / RLIF', desc: 'Life Income Fund - Convert your LIRA to generate retirement income while keeping tax advantages.' }
    },
    products: {
      title: 'Investment Products',
      subtitle: 'Diversified investment options to grow your wealth',
      gic: { title: 'GICs', desc: 'Guaranteed Investment Certificates - Safe, guaranteed returns with CDIC protection up to $100,000.' },
      mutualFunds: { title: 'Mutual Funds', desc: 'Professionally managed diversified portfolios suited to your risk tolerance and goals.' },
      etf: { title: 'ETFs', desc: 'Exchange-Traded Funds - Low-cost diversified investments that trade like stocks.' },
      segregated: { title: 'Segregated Funds', desc: 'Insurance-based investments with principal guarantees and creditor protection.' }
    },
    lending: {
      title: 'Investment Lending & Services',
      subtitle: 'Leverage and banking solutions to maximize your investment potential',
      rrspLoan: { title: 'RRSP Loan', desc: 'Borrow to maximize your RRSP contribution and get a larger tax refund to pay down the loan.' },
      investmentLoan: { title: 'Investment Loan', desc: 'Leverage borrowed funds to grow your non-registered investment portfolio. Interest may be tax-deductible.' },
      bankAccount: { title: 'Bank Account Opening', desc: 'Open high-interest savings accounts through Manulife Bank, B2B Bank, and EQ Bank.' },
      groupProducts: { title: 'Group Investment Products', desc: 'Corporate retirement and savings plans (Group RRSPs, DPSPs, TFSAs) for employees.' }
    },
    whyInvest: {
      title: 'Why Invest with Us?',
      expertGuidance: { title: 'Expert Guidance', desc: 'Professional advice tailored to your financial goals and risk tolerance' },
      trustedSolutions: { title: 'Trusted Solutions', desc: "Proven investment strategies backed by Canada's top financial institutions" },
      longTermGrowth: { title: 'Long-term Growth', desc: 'Build wealth systematically with regular reviews and portfolio rebalancing' }
    },
    cta: {
      title: 'Start Investing Today',
      subtitle: 'Schedule a consultation with our investment specialists to create your personalized plan',
      button1: 'Book a Consultation',
      button2: 'Contact Us'
    }
  },
  fr: {
    hero: {
      title: "Solutions d'Investissement",
      subtitle: 'Bâtissez votre patrimoine avec des stratégies d\'investissement intelligentes et des comptes avantageux fiscalement',
      cta: 'Prendre Rendez-vous'
    },
    registered: {
      title: 'Comptes Enregistrés Avantageux Fiscalement',
      subtitle: 'Maximisez vos économies avec des comptes d\'investissement enregistrés par le gouvernement',
      rrsp: { title: 'REER', desc: 'Régime enregistré d\'épargne-retraite - Contributions déductibles d\'impôt qui croissent à l\'abri de l\'impôt jusqu\'à la retraite.' },
      tfsa: { title: 'CELI', desc: 'Compte d\'épargne libre d\'impôt - Épargne flexible avec croissance et retraits entièrement libres d\'impôt.' },
      fhsa: { title: 'CELIAPP', desc: 'Compte d\'épargne libre d\'impôt pour l\'achat d\'une première propriété - Contributions déductibles d\'impôt pour votre premier achat.' },
      resp: { title: 'REEE', desc: 'Régime enregistré d\'épargne-études - Épargnez pour l\'éducation de votre enfant avec des subventions gouvernementales jusqu\'à 7 200$.' }
    },
    specialized: {
      title: 'Comptes Enregistrés Spécialisés',
      subtitle: 'Options enregistrées additionnelles pour besoins spécifiques',
      rdsp: { title: 'REEI', desc: 'Régime enregistré d\'épargne-invalidité - Épargne à long terme pour Canadiens handicapés, avec subventions de contrepartie gouvernementales.' },
      lira: { title: 'CRI', desc: 'Compte de retraite immobilisé - Protégez et faites croître vos fonds de pension lorsque vous quittez un employeur.' },
      lif: { title: 'FRV / FRVR', desc: 'Fonds de revenu viager - Convertissez votre CRI pour générer un revenu de retraite tout en conservant les avantages fiscaux.' }
    },
    products: {
      title: 'Produits d\'Investissement',
      subtitle: 'Options d\'investissement diversifiées pour faire croître votre patrimoine',
      gic: { title: 'CPG', desc: 'Certificats de placement garanti - Rendements sûrs et garantis avec protection SADC jusqu\'à 100 000$.' },
      mutualFunds: { title: 'Fonds Communs', desc: 'Portefeuilles diversifiés gérés professionnellement adaptés à votre tolérance au risque et vos objectifs.' },
      etf: { title: 'FNB', desc: 'Fonds négociés en bourse - Investissements diversifiés à faible coût qui se négocient comme des actions.' },
      segregated: { title: 'Fonds Distincts', desc: 'Investissements basés sur l\'assurance avec garanties du capital et protection contre les créanciers.' }
    },
    lending: {
      title: 'Prêts & Services d\'Investissement',
      subtitle: 'Solutions de levier et bancaires pour maximiser votre potentiel d\'investissement',
      rrspLoan: { title: 'Prêt REER', desc: 'Empruntez pour maximiser votre contribution REER et obtenir un remboursement d\'impôt plus important pour rembourser le prêt.' },
      investmentLoan: { title: 'Prêt d\'Investissement', desc: 'Utilisez l\'effet de levier pour faire croître votre portefeuille non enregistré. Les intérêts peuvent être déductibles d\'impôt.' },
      bankAccount: { title: 'Ouverture de Compte Bancaire', desc: 'Ouvrez des comptes d\'épargne à intérêt élevé via Manulife Bank, B2B Bank et EQ Bank.' },
      groupProducts: { title: 'Produits d\'Investissement Collectifs', desc: 'Régimes de retraite et d\'épargne collectifs (REER collectifs, RPDB, CELI) pour employés.' }
    },
    whyInvest: {
      title: 'Pourquoi Investir Avec Nous?',
      expertGuidance: { title: 'Conseils d\'Experts', desc: 'Conseils professionnels adaptés à vos objectifs financiers et tolérance au risque' },
      trustedSolutions: { title: 'Solutions de Confiance', desc: 'Stratégies d\'investissement éprouvées soutenues par les meilleures institutions financières du Canada' },
      longTermGrowth: { title: 'Croissance à Long Terme', desc: 'Bâtissez votre patrimoine systématiquement avec révisions régulières et rééquilibrage du portefeuille' }
    },
    cta: {
      title: 'Commencez à Investir Aujourd\'hui',
      subtitle: 'Planifiez une consultation avec nos spécialistes en investissement pour créer votre plan personnalisé',
      button1: 'Prendre Rendez-vous',
      button2: 'Contactez-Nous'
    }
  }
};

const InvestmentPage = () => {
  const { language } = useLanguage();
  const text = pageText[language];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-[400px] flex items-center justify-center overflow-hidden bg-[#0077B6]">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0077B6] to-[#005A8C]" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-white">{text.hero.title}</h1>
          <p className="text-lg text-blue-100 mb-6">
            {text.hero.subtitle}
          </p>
          <Button asChild size="lg" className="bg-[#D4AF37] text-[#003D5C] hover:bg-[#B8962E] font-bold">
            <Link to="/team">{text.hero.cta}</Link>
          </Button>
        </div>
      </section>

      {/* ========== SECTION 1: Registered Accounts (White Background) ========== */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-800 mb-3">{text.registered.title}</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">{text.registered.subtitle}</p>
            <div className="h-1 w-24 bg-[#D4AF37] mx-auto mt-4"></div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="bg-[#F0F7FF] border border-blue-100 hover:border-[#0077B6] transition-all hover:shadow-lg">
              <CardContent className="p-6">
                <div className="w-14 h-14 rounded-xl bg-[#D4AF37]/10 border-2 border-[#D4AF37] flex items-center justify-center mb-4">
                  <PiggyBank className="h-7 w-7 text-[#D4AF37]" />
                </div>
                <h3 className="text-lg font-bold mb-2 text-gray-800">{text.registered.rrsp.title}</h3>
                <p className="text-gray-600 text-sm mb-3">{text.registered.rrsp.desc}</p>
                <div className="space-y-1">
                  <a href="https://ia.ca/savings-plans-retirement" target="_blank" rel="noopener noreferrer" className="text-xs text-[#0077B6] hover:underline flex items-center">
                    <ExternalLink className="h-3 w-3 mr-1" />iA Financial RRSP
                  </a>
                  <a href="https://www.equitable.ca/products/investments/registered-retirement-savings-plan-rrsp" target="_blank" rel="noopener noreferrer" className="text-xs text-[#0077B6] hover:underline flex items-center">
                    <ExternalLink className="h-3 w-3 mr-1" />Equitable Life RRSP
                  </a>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#F0F7FF] border border-blue-100 hover:border-[#0077B6] transition-all hover:shadow-lg">
              <CardContent className="p-6">
                <div className="w-14 h-14 rounded-xl bg-[#D4AF37]/10 border-2 border-[#D4AF37] flex items-center justify-center mb-4">
                  <Wallet className="h-7 w-7 text-[#D4AF37]" />
                </div>
                <h3 className="text-lg font-bold mb-2 text-gray-800">{text.registered.tfsa.title}</h3>
                <p className="text-gray-600 text-sm mb-3">{text.registered.tfsa.desc}</p>
                <div className="space-y-1">
                  <a href="https://ia.ca/tfsa" target="_blank" rel="noopener noreferrer" className="text-xs text-[#0077B6] hover:underline flex items-center">
                    <ExternalLink className="h-3 w-3 mr-1" />iA Financial TFSA
                  </a>
                  <a href="https://equitable.ca/products/investments" target="_blank" rel="noopener noreferrer" className="text-xs text-[#0077B6] hover:underline flex items-center">
                    <ExternalLink className="h-3 w-3 mr-1" />Equitable Life Investments
                  </a>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#F0F7FF] border border-blue-100 hover:border-[#0077B6] transition-all hover:shadow-lg">
              <CardContent className="p-6">
                <div className="w-14 h-14 rounded-xl bg-[#D4AF37]/10 border-2 border-[#D4AF37] flex items-center justify-center mb-4">
                  <Home className="h-7 w-7 text-[#D4AF37]" />
                </div>
                <h3 className="text-lg font-bold mb-2 text-gray-800">{text.registered.fhsa.title}</h3>
                <p className="text-gray-600 text-sm mb-3">{text.registered.fhsa.desc}</p>
                <div className="space-y-1">
                  <a href="https://ia.ca/fhsa" target="_blank" rel="noopener noreferrer" className="text-xs text-[#0077B6] hover:underline flex items-center">
                    <ExternalLink className="h-3 w-3 mr-1" />iA Financial FHSA
                  </a>
                  <a href="https://www.empire.ca/investments/investment-products" target="_blank" rel="noopener noreferrer" className="text-xs text-[#0077B6] hover:underline flex items-center">
                    <ExternalLink className="h-3 w-3 mr-1" />Empire Life Investments
                  </a>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#F0F7FF] border border-blue-100 hover:border-[#0077B6] transition-all hover:shadow-lg">
              <CardContent className="p-6">
                <div className="w-14 h-14 rounded-xl bg-[#D4AF37]/10 border-2 border-[#D4AF37] flex items-center justify-center mb-4">
                  <GraduationCap className="h-7 w-7 text-[#D4AF37]" />
                </div>
                <h3 className="text-lg font-bold mb-2 text-gray-800">{text.registered.resp.title}</h3>
                <p className="text-gray-600 text-sm mb-3">{text.registered.resp.desc}</p>
                <div className="space-y-1">
                  <a href="https://ia.ca/resp" target="_blank" rel="noopener noreferrer" className="text-xs text-[#0077B6] hover:underline flex items-center">
                    <ExternalLink className="h-3 w-3 mr-1" />iA Financial RESP
                  </a>
                  <a href="https://www.empire.ca/investments/investment-products" target="_blank" rel="noopener noreferrer" className="text-xs text-[#0077B6] hover:underline flex items-center">
                    <ExternalLink className="h-3 w-3 mr-1" />Empire Life RESP
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* ========== SECTION 2: Specialized Registered Accounts (Dark Background) ========== */}
      <section className="py-12 bg-[#003D5C]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-white mb-3">{text.specialized.title}</h2>
            <p className="text-blue-200 max-w-2xl mx-auto">{text.specialized.subtitle}</p>
            <div className="h-1 w-24 bg-[#D4AF37] mx-auto mt-4"></div>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            <Card className="bg-white/10 backdrop-blur border border-white/20 hover:border-[#D4AF37] transition-all hover:shadow-lg">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-xl bg-[#D4AF37] flex items-center justify-center mb-4">
                  <Accessibility className="h-6 w-6 text-[#003D5C]" />
                </div>
                <h3 className="text-lg font-bold mb-2 text-white">{text.specialized.rdsp.title}</h3>
                <p className="text-blue-200 text-sm mb-3">{text.specialized.rdsp.desc}</p>
                <div className="space-y-1">
                  <a href="https://www.empire.ca/investments/investment-products" target="_blank" rel="noopener noreferrer" className="flex items-center text-xs text-blue-100 hover:text-[#D4AF37]">
                    <ExternalLink className="h-3 w-3 mr-1.5" />Empire Life RDSP
                  </a>
                  <a href="https://ia.ca/rdsp" target="_blank" rel="noopener noreferrer" className="flex items-center text-xs text-blue-100 hover:text-[#D4AF37]">
                    <ExternalLink className="h-3 w-3 mr-1.5" />iA Financial RDSP
                  </a>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur border border-white/20 hover:border-[#D4AF37] transition-all hover:shadow-lg">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-xl bg-[#D4AF37] flex items-center justify-center mb-4">
                  <Lock className="h-6 w-6 text-[#003D5C]" />
                </div>
                <h3 className="text-lg font-bold mb-2 text-white">{text.specialized.lira.title}</h3>
                <p className="text-blue-200 text-sm mb-3">{text.specialized.lira.desc}</p>
                <div className="space-y-1">
                  <a href="https://ia.ca/lira" target="_blank" rel="noopener noreferrer" className="flex items-center text-xs text-blue-100 hover:text-[#D4AF37]">
                    <ExternalLink className="h-3 w-3 mr-1.5" />iA Financial LIRA
                  </a>
                  <a href="https://www.empire.ca/investments/investment-products" target="_blank" rel="noopener noreferrer" className="flex items-center text-xs text-blue-100 hover:text-[#D4AF37]">
                    <ExternalLink className="h-3 w-3 mr-1.5" />Empire Life Investments
                  </a>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur border border-white/20 hover:border-[#D4AF37] transition-all hover:shadow-lg">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-xl bg-[#D4AF37] flex items-center justify-center mb-4">
                  <Landmark className="h-6 w-6 text-[#003D5C]" />
                </div>
                <h3 className="text-lg font-bold mb-2 text-white">{text.specialized.lif.title}</h3>
                <p className="text-blue-200 text-sm mb-3">{text.specialized.lif.desc}</p>
                <div className="space-y-1">
                  <a href="https://ia.ca/lif" target="_blank" rel="noopener noreferrer" className="flex items-center text-xs text-blue-100 hover:text-[#D4AF37]">
                    <ExternalLink className="h-3 w-3 mr-1.5" />iA Financial LIF
                  </a>
                  <a href="https://www.empire.ca/investments/investment-products" target="_blank" rel="noopener noreferrer" className="flex items-center text-xs text-blue-100 hover:text-[#D4AF37]">
                    <ExternalLink className="h-3 w-3 mr-1.5" />Empire Life
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* ========== SECTION 3: Investment Products (White Background) ========== */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-800 mb-3">{text.products.title}</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">{text.products.subtitle}</p>
            <div className="h-1 w-24 bg-[#D4AF37] mx-auto mt-4"></div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="bg-[#F0F7FF] border border-blue-100 hover:border-[#0077B6] transition-all hover:shadow-lg">
              <CardContent className="p-6">
                <div className="w-14 h-14 rounded-xl bg-[#0077B6]/10 border-2 border-[#0077B6] flex items-center justify-center mb-4">
                  <BadgeCheck className="h-7 w-7 text-[#0077B6]" />
                </div>
                <h3 className="text-lg font-bold mb-2 text-gray-800">{text.products.gic.title}</h3>
                <p className="text-gray-600 text-sm mb-3">{text.products.gic.desc}</p>
                <div className="space-y-1">
                  <a href="https://ia.ca/guaranteed-interest-funds" target="_blank" rel="noopener noreferrer" className="text-xs text-[#0077B6] hover:underline flex items-center">
                    <ExternalLink className="h-3 w-3 mr-1" />iA Financial GIC
                  </a>
                  <a href="https://www.empire.ca/investments/investment-products" target="_blank" rel="noopener noreferrer" className="text-xs text-[#0077B6] hover:underline flex items-center">
                    <ExternalLink className="h-3 w-3 mr-1" />Empire Life GIF
                  </a>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#F0F7FF] border border-blue-100 hover:border-[#0077B6] transition-all hover:shadow-lg">
              <CardContent className="p-6">
                <div className="w-14 h-14 rounded-xl bg-[#0077B6]/10 border-2 border-[#0077B6] flex items-center justify-center mb-4">
                  <TrendingUp className="h-7 w-7 text-[#0077B6]" />
                </div>
                <h3 className="text-lg font-bold mb-2 text-gray-800">{text.products.mutualFunds.title}</h3>
                <p className="text-gray-600 text-sm mb-3">{text.products.mutualFunds.desc}</p>
                <div className="space-y-1">
                  <a href="https://ia.ca/individuals" target="_blank" rel="noopener noreferrer" className="text-xs text-[#0077B6] hover:underline flex items-center">
                    <ExternalLink className="h-3 w-3 mr-1" />iA Financial Funds
                  </a>
                  <a href="https://www.empire.ca/investments/investment-products" target="_blank" rel="noopener noreferrer" className="text-xs text-[#0077B6] hover:underline flex items-center">
                    <ExternalLink className="h-3 w-3 mr-1" />Empire Life Funds
                  </a>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#F0F7FF] border border-blue-100 hover:border-[#0077B6] transition-all hover:shadow-lg">
              <CardContent className="p-6">
                <div className="w-14 h-14 rounded-xl bg-[#0077B6]/10 border-2 border-[#0077B6] flex items-center justify-center mb-4">
                  <BarChart3 className="h-7 w-7 text-[#0077B6]" />
                </div>
                <h3 className="text-lg font-bold mb-2 text-gray-800">{text.products.etf.title}</h3>
                <p className="text-gray-600 text-sm mb-3">{text.products.etf.desc}</p>
                <div className="space-y-1">
                  <a href="https://ia.ca/individuals/investment-products/segregated-funds" target="_blank" rel="noopener noreferrer" className="text-xs text-[#0077B6] hover:underline flex items-center">
                    <ExternalLink className="h-3 w-3 mr-1" />iA Financial Investments
                  </a>
                  <a href="https://equitable.ca/products/investments" target="_blank" rel="noopener noreferrer" className="text-xs text-[#0077B6] hover:underline flex items-center">
                    <ExternalLink className="h-3 w-3 mr-1" />Equitable Life
                  </a>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#F0F7FF] border border-blue-100 hover:border-[#0077B6] transition-all hover:shadow-lg">
              <CardContent className="p-6">
                <div className="w-14 h-14 rounded-xl bg-[#0077B6]/10 border-2 border-[#0077B6] flex items-center justify-center mb-4">
                  <Shield className="h-7 w-7 text-[#0077B6]" />
                </div>
                <h3 className="text-lg font-bold mb-2 text-gray-800">{text.products.segregated.title}</h3>
                <p className="text-gray-600 text-sm mb-3">{text.products.segregated.desc}</p>
                <div className="space-y-1">
                  <a href="https://ia.ca/individuals/investment-products/segregated-funds" target="_blank" rel="noopener noreferrer" className="text-xs text-[#0077B6] hover:underline flex items-center">
                    <ExternalLink className="h-3 w-3 mr-1" />iA Financial Seg Funds
                  </a>
                  <a href="https://www.empire.ca/investments/investment-products" target="_blank" rel="noopener noreferrer" className="text-xs text-[#0077B6] hover:underline flex items-center">
                    <ExternalLink className="h-3 w-3 mr-1" />Empire Life Seg Funds
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* ========== SECTION 4: Investment Lending & Services (Dark Background) ========== */}
      <section className="py-12 bg-[#003D5C]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-white mb-3">{text.lending.title}</h2>
            <p className="text-blue-200 max-w-2xl mx-auto">{text.lending.subtitle}</p>
            <div className="h-1 w-24 bg-[#D4AF37] mx-auto mt-4"></div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="bg-white/10 backdrop-blur border border-white/20 hover:border-[#D4AF37] transition-all hover:shadow-lg">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-xl bg-[#D4AF37] flex items-center justify-center mb-4">
                  <DollarSign className="h-6 w-6 text-[#003D5C]" />
                </div>
                <h3 className="text-lg font-bold mb-2 text-white">{text.lending.rrspLoan.title}</h3>
                <p className="text-blue-200 text-sm mb-3">{text.lending.rrspLoan.desc}</p>
                <div className="space-y-1">
                  <a href="https://www.b2bbank.com/en/products/investment-loans/rrsp-loan" target="_blank" rel="noopener noreferrer" className="flex items-center text-xs text-blue-100 hover:text-[#D4AF37]">
                    <ExternalLink className="h-3 w-3 mr-1.5" />B2B Bank RRSP Loan
                  </a>
                  <a href="https://ia.ca/advice-zone/finances/rrsp-loan" target="_blank" rel="noopener noreferrer" className="flex items-center text-xs text-blue-100 hover:text-[#D4AF37]">
                    <ExternalLink className="h-3 w-3 mr-1.5" />iA Financial
                  </a>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur border border-white/20 hover:border-[#D4AF37] transition-all hover:shadow-lg">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-xl bg-[#D4AF37] flex items-center justify-center mb-4">
                  <TrendingUp className="h-6 w-6 text-[#003D5C]" />
                </div>
                <h3 className="text-lg font-bold mb-2 text-white">{text.lending.investmentLoan.title}</h3>
                <p className="text-blue-200 text-sm mb-3">{text.lending.investmentLoan.desc}</p>
                <div className="space-y-1">
                  <a href="https://www.b2bbank.com/en/products/investment-loans" target="_blank" rel="noopener noreferrer" className="flex items-center text-xs text-blue-100 hover:text-[#D4AF37]">
                    <ExternalLink className="h-3 w-3 mr-1.5" />B2B Bank Investment Loan
                  </a>
                  <a href="https://ia.ca/advice-zone/finances/leverage-investing" target="_blank" rel="noopener noreferrer" className="flex items-center text-xs text-blue-100 hover:text-[#D4AF37]">
                    <ExternalLink className="h-3 w-3 mr-1.5" />iA Financial
                  </a>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur border border-white/20 hover:border-[#D4AF37] transition-all hover:shadow-lg">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-xl bg-[#D4AF37] flex items-center justify-center mb-4">
                  <Building className="h-6 w-6 text-[#003D5C]" />
                </div>
                <h3 className="text-lg font-bold mb-2 text-white">{text.lending.bankAccount.title}</h3>
                <p className="text-blue-200 text-sm mb-3">{text.lending.bankAccount.desc}</p>
                <div className="space-y-1">
                  <a href="https://www.b2bbank.com/en/products/banking" target="_blank" rel="noopener noreferrer" className="flex items-center text-xs text-blue-100 hover:text-[#D4AF37]">
                    <ExternalLink className="h-3 w-3 mr-1.5" />B2B Bank Accounts
                  </a>
                  <a href="https://www.eqbank.ca/personal-banking" target="_blank" rel="noopener noreferrer" className="flex items-center text-xs text-blue-100 hover:text-[#D4AF37]">
                    <ExternalLink className="h-3 w-3 mr-1.5" />EQ Bank
                  </a>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur border border-white/20 hover:border-[#D4AF37] transition-all hover:shadow-lg">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-xl bg-[#D4AF37] flex items-center justify-center mb-4">
                  <Users className="h-6 w-6 text-[#003D5C]" />
                </div>
                <h3 className="text-lg font-bold mb-2 text-white">{text.lending.groupProducts.title}</h3>
                <p className="text-blue-200 text-sm mb-3">{text.lending.groupProducts.desc}</p>
                <div className="space-y-1">
                  <a href="https://ia.ca/group" target="_blank" rel="noopener noreferrer" className="flex items-center text-xs text-blue-100 hover:text-[#D4AF37]">
                    <ExternalLink className="h-3 w-3 mr-1.5" />iA Financial Group RSP
                  </a>
                  <a href="https://www.equitable.ca/products/group-benefits" target="_blank" rel="noopener noreferrer" className="flex items-center text-xs text-blue-100 hover:text-[#D4AF37]">
                    <ExternalLink className="h-3 w-3 mr-1.5" />Equitable Life Group
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* ========== Why Invest With Us Section ========== */}
      <section className="py-12 bg-[#F0F7FF]">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-800 mb-3">{text.whyInvest.title}</h2>
            <div className="h-1 w-24 bg-[#D4AF37] mx-auto mt-3"></div>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="bg-[#0077B6]/10 border-2 border-[#0077B6] text-[#0077B6] rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="h-8 w-8" />
              </div>
              <h3 className="font-bold text-gray-800 text-lg mb-2">{text.whyInvest.expertGuidance.title}</h3>
              <p className="text-gray-600">{text.whyInvest.expertGuidance.desc}</p>
            </div>
            <div className="text-center">
              <div className="bg-[#D4AF37]/10 border-2 border-[#D4AF37] text-[#D4AF37] rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <BadgeCheck className="h-8 w-8" />
              </div>
              <h3 className="font-bold text-gray-800 text-lg mb-2">{text.whyInvest.trustedSolutions.title}</h3>
              <p className="text-gray-600">{text.whyInvest.trustedSolutions.desc}</p>
            </div>
            <div className="text-center">
              <div className="bg-[#0077B6]/10 border-2 border-[#0077B6] text-[#0077B6] rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <PiggyBank className="h-8 w-8" />
              </div>
              <h3 className="font-bold text-gray-800 text-lg mb-2">{text.whyInvest.longTermGrowth.title}</h3>
              <p className="text-gray-600">{text.whyInvest.longTermGrowth.desc}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ========== CTA Section ========== */}
      <section className="py-12 bg-[#0077B6]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-3">{text.cta.title}</h2>
          <p className="text-blue-100 mb-6 text-lg">
            {text.cta.subtitle}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-[#D4AF37] hover:bg-[#B8962E] text-[#003D5C] font-bold">
              <Link to="/team">{text.cta.button1}</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-[#0077B6] font-bold">
              <Link to="/contact">{text.cta.button2}</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default InvestmentPage;
